import React, { useCallback, useEffect, useRef, useState } from "react";
import { CalendarDays, Compass, HeartHandshake, LockKeyhole, Sparkles, Wallet, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useRole } from "@/context/RoleContext";
import { PageShell } from "@/pages/dashboard/RolePages";
import { CompleteNumerology, FullSinastry, NumberPairResult, SymbolCard } from "@/pages/dashboard/NumerologiaVisual";

const VALID_ROLES = new Set(["user", "estudiante", "consultor", "creador"]);
const TABS = [
  ["inicio", "Inicio", Compass], ["mapa", "Mi Mapa", Sparkles],
  ["hoy", "Hoy", Zap], ["vinculos", "Vínculos", HeartHandshake],
  ["calendario", "Calendario", CalendarDays],
];
const EMPTY_PROFILE = { full_name: "", birth_date: "", name_in_use: "", paternal_surname: "", maternal_surname: "", consent: false };
const dateLabel = (value) => value ? new Intl.DateTimeFormat("es-CO", {
  dateStyle: "medium", timeStyle: "short", timeZone: "America/Bogota",
}).format(new Date(value)) : "—";
const numbers = (n) => new Intl.NumberFormat("es-CO").format(n || 0);
const blankMonth = () => {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

export default function NumerologiaViva() {
  const { role } = useRole();
  const [tab, setTab] = useState("mapa");
  const [access, setAccess] = useState(null);
  const [map, setMap] = useState(null);
  const [today, setToday] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [compatibility, setCompatibility] = useState(null);
  const [sinastryStatus, setSinastryStatus] = useState(null);
  const synastryResultRef = useRef(null);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [partner, setPartner] = useState({ full_name: "", birth_date: "", name_in_use: "", paternal_surname: "", maternal_surname: "", relationship_type: "pareja", consent_confirmed: false });
  const [pairSelection, setPairSelection] = useState({ number_a: 1, number_b: 2 });
  const [pairResult, setPairResult] = useState(null);
  const [month, setMonth] = useState(blankMonth);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [clock, setClock] = useState(Date.now());
  const purchaseKey = useRef(null);
  const submitLock = useRef(false);
  const sinastrySubmitLock = useRef(false);
  const bogotaDay = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Bogota", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  const lastDay = useRef(bogotaDay());

  const active = Boolean(access?.active && Date.parse(access.expires_at) > clock);
  const preparingMap = Boolean(active && map?.profile_complete && !map?.delivered);
  const preparingSinastry = Boolean(active && sinastryStatus?.generated && !sinastryStatus?.delivered);
  const durationHours = Number(access?.duration_hours || 168);
  const durationLabel = durationHours % 24 === 0
    ? `${durationHours / 24} ${durationHours === 24 ? "día" : "días"}`
    : `${durationHours} ${durationHours === 1 ? "hora" : "horas"}`;
  const clearProtected = useCallback(() => {
    setMap(null); setToday(null); setCalendar(null); setCompatibility(null); setPairResult(null);
    setSinastryStatus(null);
    setProfile(EMPTY_PROFILE); setPartner({ full_name: "", birth_date: "", name_in_use: "", paternal_surname: "", maternal_surname: "", relationship_type: "pareja", consent_confirmed: false });
  }, []);

  const fetchAccess = useCallback(async () => {
    const { data } = await api.get("/api/numerology/access");
    setAccess(data);
    if (!data.active || Date.parse(data.expires_at) <= Date.now()) clearProtected();
    return data;
  }, [clearProtected]);

  const load = useCallback(async () => {
    setError("");
    try {
      const state = await fetchAccess();
      if (state.active && Date.parse(state.expires_at) > Date.now()) {
        const { data } = await api.get("/api/numerology/map");
        setMap(data);
        if (data.profile_complete) {
          setProfile({ ...EMPTY_PROFILE, ...data.profile, consent: true });
          if (data.delivered) {
            const { data: cycles } = await api.get("/api/numerology/today");
            setToday(cycles);
          } else {
            setToday(null);
          }
        }
        const { data: savedSinastry } = await api.get("/api/numerology/sinastry");
        setSinastryStatus(savedSinastry);
        setCompatibility(savedSinastry.delivered ? savedSinastry : null);
      }
    } catch (e) {
      setError(formatApiError(e));
      clearProtected();
    }
  }, [fetchAccess, clearProtected]);

  useEffect(() => { if (VALID_ROLES.has(role)) load(); }, [role, load]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    const onFocus = () => { setClock(Date.now()); load(); };
    window.addEventListener("focus", onFocus);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", onFocus); };
  }, [load]);

  useEffect(() => {
    if (access?.active && !active) clearProtected();
  }, [active, access, clearProtected]);

  // A date change at midnight Colombia reloads the personal day and month
  // from the canonical server; no stale "Hoy" tab is shown indefinitely.
  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(() => {
      const current = bogotaDay();
      if (current !== lastDay.current) {
        lastDay.current = current;
        load();
      }
    }, 60000);
    return () => window.clearInterval(timer);
  }, [active, load]);

  // Refresh prepared results while waiting, without exposing the server's
  // stored study until the delivery worker has committed its release.
  useEffect(() => {
    if (!active || (!preparingMap && !preparingSinastry)) return undefined;
    const timer = window.setInterval(() => { load(); }, 25000);
    return () => window.clearInterval(timer);
  }, [active, preparingMap, preparingSinastry, load]);

  // Revalidate the entitlement with the server even when a tab stays open.
  // Network failures fail closed rather than allowing offline viewing indefinitely.
  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(() => {
      fetchAccess().catch(() => { setAccess(null); clearProtected(); });
    }, 30000);
    return () => window.clearInterval(timer);
  }, [active, fetchAccess, clearProtected]);

  const buy = async () => {
    if (submitLock.current || !access || !access.available || active) return;
    submitLock.current = true;
    setBusy("purchase"); setError(""); setNotice("");
    if (!purchaseKey.current) purchaseKey.current = globalThis.crypto?.randomUUID?.() || "numerology-" + Date.now() + "-" + Math.random().toString(36).slice(2);
    try {
      await api.post("/api/numerology/subscribe", { idempotency_key: purchaseKey.current, expected_energy_cost: access.cost, expected_duration_hours: access.duration_hours });
      purchaseKey.current = null;
      setNotice("Acceso confirmado. Se descontó la Energía KD9 de tu billetera.");
      setClock(Date.now());
      await load();
      setTab("mapa");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail?.message || (typeof detail === "string" ? detail : formatApiError(e)));
      if (detail?.code === "ALREADY_ACTIVE" || detail?.code === "PRICE_CHANGED" || detail?.code === "OFFER_CHANGED") {
        purchaseKey.current = null;
        await fetchAccess().catch(() => {});
      }
    } finally { submitLock.current = false; setBusy(""); }
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy("profile"); setError(""); setNotice("");
    try {
      await api.put("/api/numerology/profile", profile);
      setNotice("Datos recibidos. Tu estudio fue calculado y está en preparación. Se entregará automáticamente cuando se cumplan las cuatro horas.");
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const showCalendar = async (selection = month) => {
    setBusy("calendar"); setError("");
    try {
      const { data } = await api.get("/api/numerology/calendar", { params: selection });
      setCalendar(data);
    } catch (e) {
      setError(formatApiError(e));
      if (e?.response?.status === 403) await fetchAccess().catch(() => {});
    } finally { setBusy(""); }
  };

  const compare = async (event) => {
    event.preventDefault();
    if (sinastrySubmitLock.current || !sinastryStatus || sinastryStatus.generated) return;
    sinastrySubmitLock.current = true;
    setBusy("compare"); setError(""); setNotice("");
    try {
      const { data } = await api.post("/api/numerology/compatibility", partner);
      setCompatibility(data.delivered ? data : null);
      setSinastryStatus(data);
      setNotice(data.delivered ? "Tu sinastría está terminada y entregada." : "Tu sinastría fue calculada. Se entregará automáticamente a la hora indicada. Si la solicitaste después de las primeras cuatro horas desde la compra, tendrá su propio plazo de preparación de cuatro horas.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // A lost response or a simultaneous request may already have consumed
      // the single generation. Retrieve that same result, never make a new one.
      try {
        const { data: stored } = await api.get("/api/numerology/sinastry");
        setSinastryStatus(stored);
        if (stored.generated) {
          setCompatibility(stored.delivered ? stored : null);
          setNotice(stored.delivered ? "Tu sinastría ya fue entregada. Puedes consultar el resultado." : "Tu sinastría ya está en preparación. Se habilitará cuando llegue su hora de entrega.");
        } else {
          setError(detail?.message || formatApiError(e));
        }
      } catch {
        setError(detail?.message || formatApiError(e));
      }
      if (e?.response?.status === 403) await fetchAccess().catch(() => {});
    } finally {
      sinastrySubmitLock.current = false;
      setBusy("");
    }
  };

  useEffect(() => {
    if (compatibility && active) {
      const handle = window.requestAnimationFrame(() => {
        synastryResultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return () => window.cancelAnimationFrame(handle);
    }
    return undefined;
  }, [compatibility, active]);

  const compareNumbers = async () => {
    setBusy("numbers"); setError("");
    try {
      const { data } = await api.post("/api/numerology/number-pair", pairSelection);
      setPairResult(data);
    } catch (e) { setError(formatApiError(e)); if (e?.response?.status === 403) await fetchAccess().catch(() => {}); }
    finally { setBusy(""); }
  };

  if (!VALID_ROLES.has(role)) return <PageShell title="Numerología Viva KD9"><p>Esta función está reservada a Usuario, Estudiante, Consultor y Creador.</p></PageShell>;

  return <PageShell title="Numerología Viva KD9" desc="Un mapa simbólico de tus números, ciclos y vínculos.">
    <div className="max-w-5xl mx-auto space-y-5" data-testid="numerology-viva-root">
      <div className="kd9-card p-5 rounded-2xl border border-amber-400/25 bg-black/20">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><p className="text-xs tracking-widest text-amber-300">NUMEROLOGÍA VIVA · ACCESO PREPAGO</p>
            <h2 className="text-2xl font-cinzel mt-1">{numbers(access?.cost ?? 100)} ⚡ KD / {durationLabel}</h2>
          </div>
          <div className="text-sm text-white/70">{access
            ? active ? <span className="text-green-400">Activo hasta {dateLabel(access.expires_at)}</span>
              : <span className="text-amber-300">{access.expires_at ? "Acceso vencido · requiere renovación" : "Acceso no activado"}</span>
            : "Comprobando acceso..."}</div>
        </div>
        <div className="text-sm text-white/70 mt-3">Sin renovación automática. El plazo corre desde la compra y vence en {durationHours} horas. Saldo: <strong>{access ? numbers(access.balance) : "—"} KD</strong>.</div>
      </div>

      {notice && <div role="status" className="rounded-lg border border-green-500/30 px-4 py-3 text-sm text-green-300">{notice}</div>}
      {error && <div role="alert" className="rounded-lg border border-red-500/40 px-4 py-3 text-sm text-red-300">{error}</div>}

      {!active ? <div className="kd9-card rounded-2xl p-6 text-center space-y-4" data-testid="numerology-locked">
        <LockKeyhole size={38} className="mx-auto text-amber-300" aria-hidden="true"/>
        <h3 className="font-cinzel text-xl">Desbloquea Numerología Viva KD9</h3>
        <p className="text-sm text-white/70 max-w-xl mx-auto">El acceso dura {durationLabel}. Los estudios numerológicos se calculan al proporcionar los datos, pero se entregan después de cuatro horas de preparación. Si solicitas una sinastría más tarde, también se respetará ese tiempo de elaboración. Podrás consultarlos tras la entrega y hasta el vencimiento de tu compra.</p>
        <button type="button" className="kd9-btn-primary min-h-12 rounded-xl px-6 disabled:opacity-50"
          disabled={!access || !access.available || Boolean(busy) || access.wallet_frozen || access.balance < access.cost} onClick={buy}
          data-testid="numerology-purchase-button">
          <Wallet size={17} className="inline mr-2"/>{busy === "purchase" ? "Procesando..." : `Activar ${durationLabel} · ${numbers(access?.cost ?? 100)} KD`}
        </button>
        {access && !access.available && <p className="text-sm text-amber-200">Las compras están desactivadas por el Director. Tus datos permanecen guardados.</p>}
        {access && access.available && (access.balance < access.cost || access.wallet_frozen) && <p className="text-sm text-amber-200">Saldo insuficiente o billetera bloqueada. <Link to="/app/energia-kd" className="underline">Ir a Energía KD9</Link></p>}
      </div> : <>
        <nav className="grid grid-cols-3 sm:grid-cols-5 gap-2" aria-label="Secciones de Numerología Viva">
          {TABS.map(([id, label, Icon]) => <button key={id} type="button" onClick={() => setTab(id)}
            aria-current={tab === id ? "page" : undefined}
            className={(tab === id ? "kd9-btn-primary" : "kd9-btn-outline") + " rounded-xl px-2 py-3 min-h-12 text-xs"}>
            <Icon size={15} className="inline mr-1"/>{label}
          </button>)}
        </nav>

        {tab === "inicio" && <div className="kd9-card p-6 space-y-4">
          <h3 className="text-xl font-cinzel">Tu universo numerológico</h3>
          <p className="text-sm text-white/70">Los cálculos se basan en fecha y nombre; las correspondencias son simbólicas y no determinan tu destino ni diagnostican enfermedades.</p>
          <p className="text-sm">Acceso activo hasta el <strong>{dateLabel(access.expires_at)}</strong>. Puedes consultar tus datos mientras tu acceso esté vigente.</p>
          <button className="kd9-btn-primary px-5 py-3 rounded-xl" onClick={() => setTab("mapa")}>Abrir mi mapa</button>
        </div>}

        {tab === "mapa" && <div className="space-y-4">
          {preparingMap && <div className="kd9-card p-5 sm:p-7 space-y-3" data-testid="numerology-study-pending">
            <h3 className="font-cinzel text-xl text-amber-300">Numerología en preparación</h3>
            <p className="text-[15px] leading-7 text-white/85">
              Tu estudio completo ya fue calculado y permanece reservado. Los resultados no se muestran ni se envían antes de la hora de entrega. Cuando finalice el plazo, aparecerá automáticamente aquí como <strong>«Numerología terminada y entregada»</strong> y recibirás una notificación en KD9 y por WhatsApp si tu número está verificado y autorizaste los mensajes.
            </p>
            <p className="text-sm text-amber-200">Entrega estimada: {dateLabel(map?.delivery?.delivery_at)}</p>
          </div>}
          {map?.delivered && <p className="text-sm text-green-300" data-testid="numerology-study-delivered">Numerología terminada y entregada.</p>}
          {!map?.profile_complete && <form onSubmit={save} className="kd9-card p-5 space-y-3">
            <h3 className="font-cinzel text-lg">{map?.profile_complete ? "Datos del estudio registrado" : "Crear mi mapa numerológico"}</h3>
            <label className="block text-sm">Nombre completo de nacimiento
              <input className="kd9-input mt-1 w-full" value={profile.full_name} required minLength={3} onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))}/>
            </label>
            <label className="block text-sm">Fecha de nacimiento
              <input type="date" className="kd9-input mt-1 w-full" value={profile.birth_date} required onChange={e => setProfile(p => ({ ...p, birth_date: e.target.value }))}/>
            </label>
            <div className="grid sm:grid-cols-3 gap-3">
              {[["name_in_use","Nombre de uso (opcional)"],["paternal_surname","Primer apellido"],["maternal_surname","Segundo apellido"]].map(([key,label])=><label className="block text-sm" key={key}>{label}
                <input className="kd9-input mt-1 w-full" value={profile[key] || ""} onChange={e => setProfile(p=>({...p,[key]:e.target.value}))}/></label>)}
            </div>
            <label className="flex gap-2 text-xs text-white/70"><input type="checkbox" required checked={profile.consent} onChange={e=>setProfile(p=>({...p,consent:e.target.checked}))}/>
              Autorizo almacenar mis datos para calcular mi mapa. Podré consultarlos mientras mi suscripción esté activa.</label>
            <button type="submit" disabled={Boolean(busy)} className="kd9-btn-primary px-5 py-3 rounded-xl disabled:opacity-50">{busy === "profile" ? "Calculando..." : "Generar estudio numerológico completo"}</button>
          </form>}
          {map?.delivered && <CompleteNumerology map={map} today={today}/>}
          {map?.profile_complete && !map?.delivered && <p className="text-sm text-amber-200">El informe completo queda bloqueado hasta la entrega. Tus datos se guardaron correctamente.</p>}
        </div>}

        {tab === "hoy" && <section className="space-y-5" data-testid="numerology-today-report">
          <div className="kd9-card rounded-2xl p-5 sm:p-7 space-y-3">
            <h3 className="font-cinzel text-xl text-amber-300">Tus ciclos personales de hoy</h3>
            <p className="text-[15px] sm:text-base leading-7 sm:leading-8 text-white/85">
              Tu Año, Mes y Día Personal muestran tres escalas de tiempo distintas. Cada número
              conserva su cálculo, mientras que la lectura KD9 explica su significado, las
              situaciones que podrías reconocer y cómo llevar esa reflexión a decisiones reales.
              No describen acontecimientos inevitables.
            </p>
            {today?.reference_date && <p className="text-xs text-amber-200/75">
              Fecha de referencia: {today.reference_date} · Hora local de Colombia
            </p>}
          </div>
          {today && map?.delivered ? <>
            <SymbolCard title="Año personal · Tu proceso actual" value={today.year}/>
            <SymbolCard title="Mes personal · Lo que puedes trabajar este mes" value={today.month}/>
            <SymbolCard title="Día personal · Tu reflexión de hoy" value={today.day}/>
            <section className="kd9-card rounded-2xl p-5 sm:p-7 space-y-4" data-testid="numerology-today-integration">
              <h3 className="font-cinzel text-xl text-amber-300">Cómo se conectan tus tres ciclos</h3>
              {(today.integration?.narrative || []).map((paragraph, index) =>
                <p key={index} className="text-[15px] sm:text-base leading-7 sm:leading-8 text-white/85">{paragraph}</p>
              )}
              <p className="text-xs text-white/60">
                Interpretación simbólica KD9; no sustituye la información sobre las circunstancias
                reales de la persona ni constituye un diagnóstico.
              </p>
            </section>
          </> : <div className="kd9-card rounded-2xl p-5">
            <p className="text-[15px] leading-7 text-white/75">
              {preparingMap ? "Tus ciclos también estarán disponibles cuando el estudio se entregue automáticamente." : "Completa tu nombre y fecha de nacimiento en Mi Mapa para calcular tus ciclos personales."}
            </p>
          </div>}
        </section>}

        {tab === "calendario" && <div className="kd9-card p-5 space-y-4">
          <h3 className="font-cinzel text-xl">Calendario de ciclos</h3>
          <div className="flex flex-wrap gap-3">
            <label>Mes <select className="kd9-input ml-2" value={month.month} onChange={e=>setMonth(v=>({...v,month:Number(e.target.value)}))}>
              {Array.from({length:12},(_,i)=><option value={i+1} key={i+1}>{i+1}</option>)}</select></label>
            <label>Año <input type="number" className="kd9-input ml-2 w-28" min="1900" max="2200" value={month.year} onChange={e=>setMonth(v=>({...v,year:Number(e.target.value)}))}/></label>
            <button className="kd9-btn-primary px-5 py-2 rounded-xl" disabled={!map?.delivered || busy==="calendar"} onClick={()=>showCalendar()}>Ver ciclos</button>
          </div>
          {!map?.delivered && <p>El calendario se habilitará al entregar el estudio numerológico.</p>}
          {calendar && <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
            {calendar.days.map(d=><div key={d.date} className="rounded-lg border border-amber-400/25 p-2 text-center"><div className="text-xs text-white/70">{d.date.slice(-2)}</div><strong className="font-cinzel text-amber-300">{d.day.notation}</strong></div>)}
          </div>}
        </div>}

        {tab === "vinculos" && <div className="space-y-4">
          {sinastryStatus?.generated ? <div className="kd9-card p-5 sm:p-7 space-y-3" data-testid="sinastry-used-notice">
            <h3 className="font-cinzel text-xl text-amber-300">Tu sinastría de esta compra ya fue generada</h3>
            <p className="text-[15px] leading-7 text-white/80">Cada compra permite generar una única sinastría entre dos personas. {sinastryStatus?.delivered
              ? "Tu resultado está terminado y entregado. Puedes leerlo todas las veces que desees hasta el vencimiento."
              : "Tu sinastría ya fue calculada y se encuentra en preparación. Solo se mostrará después de las cuatro horas correspondientes, con notificación automática de entrega."} Para generar otra sinastría necesitarás una nueva compra.</p>
            {!sinastryStatus?.delivered && <p className="text-sm text-amber-200" data-testid="sinastry-pending">Entrega estimada: {dateLabel(sinastryStatus?.delivery?.delivery_at)}</p>}
            {sinastryStatus?.delivered && <p className="text-sm text-green-300">Sinastría terminada y entregada.</p>}
            <p className="text-sm text-amber-200">Generaciones restantes: 0 de 1.</p>
          </div> : sinastryStatus ? <form className="kd9-card p-5 space-y-3" onSubmit={compare}>
            <p className="text-sm text-amber-200" data-testid="sinastry-quota">Esta compra incluye 1 generación de sinastría. Confirma los datos antes de continuar; el resultado quedará disponible hasta el vencimiento de tu acceso.</p>
            <h3 className="font-cinzel text-xl">Sinastría KD9 · Persona A y Persona B</h3>
            <div className="rounded-xl border border-amber-400/25 p-3 bg-black/20 text-sm">
              <p className="text-amber-300 font-cinzel">Persona A · tu mapa ya registrado</p>
              <p className="text-white/80 mt-1">{map?.profile?.full_name || "Registra primero tu nombre y fecha de nacimiento en Mi Mapa"}</p>
              <p className="text-white/60 text-xs">Fecha de nacimiento: {map?.profile?.birth_date || "pendiente"}</p>
            </div>
            <label className="block text-sm">Tipo de vínculo
              <select className="kd9-input w-full mt-1" value={partner.relationship_type}
                onChange={e=>setPartner(p=>({...p,relationship_type:e.target.value}))}>
                {[["pareja","Pareja"],["familia","Familia"],["amistad","Amistad"],["sociedad","Sociedad o proyectos"],["terapeutica","Terapéutico"],["otro","Otro"]].map(([value,label])=><option value={value} key={value}>{label}</option>)}
              </select>
            </label>
            <p className="text-amber-300 font-cinzel">Persona B · introduce sus datos autorizados</p>
            <p className="text-xs text-white/70">Compara dos mapas de manera simbólica; no determina la calidad de una relación.</p>
            <label className="block text-sm">Nombre completo de la segunda persona
              <input className="kd9-input w-full mt-1" value={partner.full_name} required minLength={3} onChange={e=>setPartner(v=>({...v,full_name:e.target.value}))}/>
            </label>
            <label className="block text-sm">Fecha de nacimiento de la segunda persona
              <input className="kd9-input w-full mt-1" type="date" value={partner.birth_date} required onChange={e=>setPartner(v=>({...v,birth_date:e.target.value}))}/>
            </label>
            <div className="grid sm:grid-cols-3 gap-3">
              {[[ "name_in_use", "Nombre de uso (opcional)" ],[ "paternal_surname","Primer apellido (opcional)" ],[ "maternal_surname","Segundo apellido (opcional)" ]].map(([key,label])=><label className="block text-sm" key={key}>{label}
                <input className="kd9-input mt-1 w-full" value={partner[key] || ""}
                  onChange={e=>setPartner(p=>({...p,[key]:e.target.value}))}/>
              </label>)}
            </div>
            <label className="text-xs flex gap-2"><input type="checkbox" required checked={partner.consent_confirmed} onChange={e=>setPartner(v=>({...v,consent_confirmed:e.target.checked}))}/>Confirmo que tengo autorización para utilizar los datos de la otra persona y conservar únicamente la interpretación conjunta hasta que venza este acceso. Su nombre y fecha no se guardarán en el resultado.</label>
            <button type="submit" disabled={!map?.profile_complete || !sinastryStatus || sinastryStatus.generated || Boolean(busy)} className="kd9-btn-primary px-5 py-3 rounded-xl">{busy==="compare"?"Calculando relación...":"Generar resultado de sinastría"}</button>
          </form> : <div className="kd9-card rounded-xl p-5 text-sm text-white/70">Comprobando el derecho de sinastría de tu compra...</div>}
          <div ref={synastryResultRef} className="scroll-mt-36">
            <FullSinastry report={compatibility}/>
          </div>
          <section className="kd9-card p-5 space-y-3">
            <h3 className="font-cinzel text-lg text-amber-300">Comparar cualquier número A ↔ B</h3>
            <p className="text-xs text-white/65">Este comparador educativo de números del 1 al 9 es independiente de la sinastría personalizada. No crea otra sinastría entre personas.</p>
            <div className="grid grid-cols-2 gap-3">
              {[["number_a","Número A"],["number_b","Número B"]].map(([key,label])=><label key={key} className="text-sm">{label}
                <select className="kd9-input w-full mt-1" value={pairSelection[key]} onChange={e=>setPairSelection(p=>({...p,[key]:Number(e.target.value)}))}>
                  {Array.from({length:9},(_,i)=><option value={i+1} key={i+1}>{i+1}</option>)}
                </select>
              </label>)}
            </div>
            <button type="button" className="kd9-btn-outline rounded-xl min-h-12 px-5" disabled={Boolean(busy)} onClick={compareNumbers}>
              Comparar números
            </button>
            <NumberPairResult result={pairResult}/>
          </section>
        </div>}

        <p className="text-xs text-white/60">Sistema simbólico de reflexión. No reemplaza medicina, psicología, asesoría jurídica o financiera. Los cálculos matemáticos se separan de las interpretaciones y las hipótesis se verifican con la experiencia de la persona.</p>
      </>}
    </div>
  </PageShell>;
}
